import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import Header from './Header/Header.component';
import Sidebar from './Sidebar/Sidebar.component';
import { isUserAuthenticated } from '@/helpers/auth';
import { KnockFeedProvider } from '@knocklabs/react-notification-feed';

import NotificationProvider from '@/providers/NotificationProvider';
import { useUserStore } from '@/store/Userdetails';
import { shallow } from 'zustand/shallow';
import { useWebSocketStore } from '@/store/WebSocketStore';
import { addVisibilityChangeListener } from '@/hooks/visibility';

const SOCKET_URL =  process.env.REACT_APP_WEB_SOCKET;
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;

const Layout = () => {
  const socketRef = useRef(null);
  const [ isConnected, setIsConnected ] = useState(false);
  const [ isMobileSidebarOpen, setIsMobileSidebarOpen ] = useState(false);
  const openSidebar = useCallback(() => setIsMobileSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setIsMobileSidebarOpen(false), []);
  const { userData } = useUserStore(
    (state) => ({
      userData: state.userData,
    }),
    shallow
  );

  const { setSwebSocketData, setSwebSocketDataDashBoard } = useWebSocketStore(
    (state) => ({
      setSwebSocketData: state.setSwebSocketData,
      islogin: state.islogin,
      setIslogin: state.setIslogin,
      setSwebSocketDataDashBoard:state.setSwebSocketDataDashBoard,
    }),
    shallow
  );

  const reconnectTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);

  const cancelReconnect = () => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  // Every deliberate close (offline, logout, unmount) must go through here, or `onclose` treats it as a dropped connection and reconnects behind our back.
  const closeSocket = () => {
    shouldReconnectRef.current = false;
    cancelReconnect();

    // null the ref before close() rather than waiting for the onclose to prevent a race timing issue with a mid-close online event then onclose refusing to retry due to the flag being falsey
    const ws = socketRef.current;
    socketRef.current = null;
    if (ws) {
      ws.close();
    }
  };

  const scheduleReconnect = () => {
    if (reconnectTimerRef.current !== null) {
      return;
    }

    const delay = Math.min(RECONNECT_BASE_DELAY * 2 ** reconnectAttemptsRef.current, RECONNECT_MAX_DELAY); // exponentially increase but clamp to max

    // Half-to-full jitter so a gateway outage doesn't bring every open tab back in lockstep.
    const jittered = delay / 2 + Math.random() * (delay / 2);
    reconnectAttemptsRef.current += 1;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      connectWebSocket();
    }, jittered);
  };

  const connectWebSocket = () => {
    if (!isUserAuthenticated() || socketRef.current !== null) {
      return;
    }

    shouldReconnectRef.current = true;
    const ws = new WebSocket(SOCKET_URL);
    ws.onopen = () => {
      // A socket replaced while it was still connecting must not claim the connection.
      if (socketRef.current !== ws) {
        ws.close();
        return;
      }

      reconnectAttemptsRef.current = 0;
      setIsConnected(true);
      const id = useUserStore.getState().userData?.data?.responseData?.userData?.user[0]?.id;
      ws.send(`{"action": "join", "userId" : ${id}, "activityStatus": "LOGIN"}`);
    };

    ws.onmessage = (event) => {
      const message = event.data;
      let data = JSON.parse(message);
      if (data?.status === 'LOGIN' || data?.status === 'LOGOUT') {
        setSwebSocketData(data);
      }

      if (data.eventType === 'substatuschange' || data.eventType === 'legassignmentchange') {
        setSwebSocketDataDashBoard(data);
      }
    };

    ws.onclose = () => {
      // A newer socket already owns the ref, nulling it here would orphan that one.
      if (socketRef.current !== null && socketRef.current !== ws) {
        return;
      }
      setIsConnected(false);
      socketRef.current = null;

      if (shouldReconnectRef.current && navigator.onLine && isUserAuthenticated()) {
        scheduleReconnect();
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error', error);
      ws.close(); // Close socket to trigger onclose
    };

    socketRef.current = ws;
  };

  useEffect(() => {
    const handleOnline = () => {
      // Coming back online is new information, so retry immediately rather than waiting out a backoff delay that was earned while the network was down.
      reconnectAttemptsRef.current = 0;
      cancelReconnect();
      // decide from the ref, never from `isConnected` as that is captured at its render and could be inaccurate here
      connectWebSocket();
    };

    const handleOffline = () => {
      setIsConnected(false);
      closeSocket(); // Close socket on internet disconnection
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isConnected ]);

  useEffect(() => {
    // Create an interval ID to keep reference to the interval for clearing it later
    const intervalId = setInterval(() => {
      const token = localStorage.getItem('token');
      if (token === null || token === undefined) {
        // Close socket on token absence
        closeSocket();
      }
    }, 500);

    // Cleanup function to clear the interval when the component unmounts or before the effect runs again
    return () => {
      clearInterval(intervalId);
      // Close socket on cleanup
      closeSocket();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isConnected) {
      if (userData?.data?.responseData?.userData?.user[0]?.id) {
        connectWebSocket();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ SOCKET_URL, userData?.data?.responseData?.userData?.user[0]?.id ]);

  useEffect(() => {
    // Declare intervalId outside the listener callback scope to maintain the reference
    let intervalId = 1;
    const startInt = () => {
      // Set a new interval when the page becomes visible
      intervalId = setInterval(() => {
        // Read userId from the live store rather than the captured closure —
        // the enclosing useEffect has empty deps, so users who log in after
        // mount would otherwise heartbeat with undefined.
        const id = useUserStore.getState().userData?.data?.responseData?.userData?.user[0]?.id;
        // Skip heartbeat while the socket is still in CONNECTING after a reconnect —
        // send() throws synchronously on a non-OPEN socket, which loses the heartbeat
        // and surfaces as the "Still in CONNECTING state" error in Atatus.
        if (socketRef.current?.readyState === WebSocket.OPEN && (id !== null && id !== undefined)) {
          socketRef.current.send(JSON.stringify({
            action: 'heartbeat',
            userId: id,
            activityStatus: 'LOGIN',
          }));
        }
      }, 100000); // every 100 seconds
    };
    if (intervalId === 1) {
      startInt();
    }
    const removeVisibilityChangeListener = addVisibilityChangeListener((isVisible) => {
      // Clear any existing intervals when visibility changes
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      if (isVisible) {
        if (!socketRef.current) {
          if (userData?.data?.responseData?.userData?.user[0]?.id) {
            connectWebSocket();
          }
        }
        startInt();
      }
    });

    // Clean up the event listener and clear the interval on component unmount
    return () => {
      removeVisibilityChangeListener();
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return isUserAuthenticated() ? (
    <>
      {userData?.data?.responseData?.ad_access_token && (
        <KnockFeedProvider apiKey={process.env.REACT_APP_KNOCK_API_KEY} feedId={process.env.REACT_APP_KNOCK_FEED_ID} userId={userData?.data?.responseData?.userData?.userAzurAD[0].azure_ad}>
          <NotificationProvider />
        </KnockFeedProvider>
      )}
      <Sidebar isMobileOpen={isMobileSidebarOpen} onMobileClose={closeSidebar}>
        <Header onOpenSidebar={openSidebar} />
        <div>
          <Outlet />
        </div>
      </Sidebar>
    </>
  ) : (
    <Navigate to={`/login?return_url=${encodeURIComponent(window.location.pathname)}`} replace />
  );
};

export default Layout;
